import React, { useState, useEffect, memo } from 'react'
import './MobiScrollCalendar.scss'
import {
  CRow,
  CCol,
  CContainer,
  CInputGroup,
  CFormInput,
  CInputGroupText,
  CFormLabel,
} from '@coreui/react-pro'
import { CalendarNav, setOptions, Datepicker, CalendarPrev, CalendarNext } from '@mobiscroll/react'
import '@mobiscroll/react/dist/css/mobiscroll.scss'
import { toast } from 'react-toastify'

import { format, isWithinInterval } from 'date-fns'
import { useSelector } from 'react-redux'
import moment from 'moment'
import InputMask from 'react-input-mask'
import { dateUSFormat } from '../../../utils/dateTimeUtils'
import { getTheme } from '../../../redux/user/userSlice'
import { createCalendarDotArray } from '../../../utils/sharedCalendarFunctions'
import { GRAY_300 } from '../../../scss/colorConsts'
import {
  oneHundredFiftyYearsAgo,
  oneYearFromNow,
  twentyfiveMonthsAgo,
  twoYearsAgo,
} from './mobiscrollConstants'

setOptions({
  theme: 'ios',
  themeVariant: 'light',
})

function validateTimestamp(date, type, min) {
  return isWithinInterval(new Date(date), {
    start: new Date(
      type === 'CENSUS'
        ? min || twoYearsAgo
        : type === 'PIQ'
          ? twentyfiveMonthsAgo
          : oneHundredFiftyYearsAgo,
    ),
    end: new Date(oneYearFromNow),
  })
}

function showDateError(handleDateSelect, type) {
  toast.dismiss()
  let message =
    "The date of service for this encounter can't be more than two years in the past, or one year in the future"
  if (type === 'DOB') {
    message = "The DOB can't be more than 150 years ago, or in the future"
  } else if (type === 'PCC') {
    message = "The chosen date can't be more than 150 years ago, or in the future"
  } else if (type === 'PIQ') {
    message = "The custom date can't be more than 25 months in the past"
  }
  toast.error(message)
  handleDateSelect(moment().format('MM/DD/yyyy'))
}

const MobiScrollCalendar = ({
  value,
  label,
  census,
  placeholder,
  disabled,
  invalid = [],
  isOpen,
  setDatePickerFlag,
  setSelectedDateRange,
  handleDateChange,
  handleMonthChange,
  facility_id,
  data,
  originalCalendarData = [],
  customDate,
  type = 'CENSUS',
  max,
  min,
}) => {
  const theme = useSelector(getTheme)
  const [dateValue, setDateValue] = useState(value)
  const [tdateValue, setTDateValue] = useState('')

  useEffect(() => {
    setDateValue(value)
  }, [value, disabled])

  const [opened, setOpened] = useState(false)
  useEffect(() => {
    if (opened === true) {
      setTDateValue('')
    }
  }, [opened])

  const handleDateSelect = (e) => {
    setDateValue(e)
    if (handleDateChange) {
      handleDateChange(e)
    }
  }

  const [markedCalendarDays, setMarkedCalendarDays] = useState([])

  useEffect(() => {
    if (data) {
      setMarkedCalendarDays(createCalendarDotArray(data, facility_id))
    }
  }, [data, facility_id])

  const customWithNavButtons = () => {
    return (
      <CContainer>
        <CRow className="row">
          <CCol className="col-12 text-center">
            <InputMask
              id="date-box"
              className={'mbsc-date-box'}
              style={{
                border: '1px solid var(--mbsc-calendar-date-box-border)',
                color: 'var(--default-text-color)',
                backgroundColor: 'var(--mobiscroll-bg-color)',
                textAlign: 'center',
              }}
              mask="99/99/9999"
              placeholder="MM/DD/YYYY"
              onChange={($event) => {
                let dateValue
                dateValue = $event.target.value
                setTDateValue(dateValue)
                if (dateValue.length === 10 && !dateValue.includes('_')) {
                  const parsedDate = moment(dateValue)
                  const isValidDate = parsedDate.isValid()
                  if (
                    isValidDate &&
                    dateValue.length === 10 &&
                    validateTimestamp(parsedDate, type, min)
                  ) {
                    handleDateSelect(moment(dateValue).format('MM/DD/yyyy'))
                  } else {
                    showDateError(handleDateSelect, type)
                  }
                }
              }}
              value={
                tdateValue == '__/__/____' &&
                tdateValue != 'Invalid Date' &&
                tdateValue != undefined &&
                !moment(tdateValue, 'YYYY-MM-DD').isValid()
                  ? tdateValue
                  : tdateValue
              }
            />
          </CCol>
        </CRow>
        <CRow className="row calendar-nav">
          <CCol className="col-2">
            <CalendarPrev />
          </CCol>
          <CCol className="col-7">
            <CalendarNav />
          </CCol>
          <CCol className="col-3">
            <CalendarNext />
          </CCol>
        </CRow>
      </CContainer>
    )
  }

  if (customDate) {
    return (
      <Datepicker
        className="calendardiv-encounterHistory"
        controls={['calendar']}
        select={'range'}
        themeVariant={theme}
        isOpen={isOpen}
        rangeHighlight={false}
        min={type === 'PIQ' ? twentyfiveMonthsAgo : ''}
        max={dateValue}
        maxRange={365}
        touchUi={true}
        onCancel={() => {
          if (setDatePickerFlag) {
            setDatePickerFlag(false)
          }
        }}
        onChange={(event) => {
          setSelectedDateRange({
            startDate: event.value[0],
            endDate: event.value[1],
          })
          handleDateSelect(event.valueText)
          if (setDatePickerFlag) {
            setDatePickerFlag(false)
          }
        }}
      />
    )
  }

  return (
    <div>
      {label && <CFormLabel className="label">{label}</CFormLabel>}

      {census ? (
        <CInputGroup className="census-date-input">
          <CInputGroupText
            style={{
              border: `0px solid ${GRAY_300}`,
              backgroundColor: ' var(--census-header-date-picker-background)',
            }}
            className={`custom-date-input form`}
          >
            <i
              className="fa-regular fa-calendar"
              onClick={() => !disabled && setOpened(true)}
              style={{ color: 'var(--default-text-color)' }}
            ></i>
          </CInputGroupText>
          <div className="date-box-holder">
            <div
              className="back-btn"
              onClick={() => {
                if (value) {
                  handleDateSelect(moment(value).subtract(1, 'days').format('MM/DD/yyyy'))
                }
              }}
            >
              <i className="fal fa-caret-left icon" style={{ fontSize: '1.5em' }}></i>
            </div>
            <CFormInput
              style={{ border: '0px' }}
              onChange={($event) => {
                if (validateTimestamp($event.target.value, type, min)) {
                  handleDateSelect($event.target.value)
                } else {
                  showDateError(handleDateSelect, type)
                }
              }}
              value={
                format(new Date(), 'yyyy-MM-dd') === value
                  ? format(new Date(), "'Today', MM/dd/yyyy")
                  : dateUSFormat(value)
              }
              onClick={() => setOpened(true)}
            />
            <div
              className="forward-btn"
              onClick={() => {
                if (value) {
                  handleDateSelect(moment(value).add(1, 'days').format('MM/DD/yyyy'))
                }
              }}
            >
              <i className="fal fa-caret-right icon" style={{ fontSize: '1.5em' }}></i>
            </div>
          </div>
        </CInputGroup>
      ) : (
        <CInputGroup className="" style={{ border: `1px solid ${GRAY_300}`, borderRadius: '8px' }}>
          <InputMask
            mask="99/99/9999"
            className="form-control date-input "
            placeholder={placeholder || 'MM/DD/YYYY'}
            onChange={($event) => {
              let dateValue
              dateValue = $event.target.value
              setDateValue(dateValue)
              setTDateValue(dateValue)
              if (dateValue.length === 10 && !dateValue.includes('_')) {
                const parsedDate = moment(dateValue)
                const isValidDate = parsedDate.isValid()
                if (
                  isValidDate &&
                  dateValue.length === 10 &&
                  validateTimestamp(dateValue, type, min)
                ) {
                  handleDateSelect(moment(dateValue).format('MM/DD/yyyy'))
                } else {
                  showDateError(handleDateSelect, type)
                }
              }
            }}
            value={dateValue}
            disabled={disabled}
          />
          <CInputGroupText
            style={{
              border: `0px solid ${GRAY_300}`,
            }}
            onClick={() => {
              disabled ? setOpened(false) : setOpened(true)
            }}
            className={`custom-date-input`}
          >
            <i
              className="fa-regular fa-calendar"
              onClick={() => setOpened(true)}
              style={{ color: 'var(--default-text-color)' }}
            ></i>
          </CInputGroupText>
        </CInputGroup>
      )}

      {opened && (
        <Datepicker
          className="calendardiv-encounterHistory"
          controls={['calendar']}
          select={'date'}
          themeVariant={theme}
          isOpen={opened}
          display={'bubble'}
          calendarType={'month'}
          pages={1}
          placeholder={placeholder || ''}
          renderCalendarHeader={customWithNavButtons}
          touchUi={true}
          min={
            type === 'CENSUS' ? min || twoYearsAgo : type === 'DOB' ? oneHundredFiftyYearsAgo : ''
          }
          max={type === 'CENSUS' ? max || oneYearFromNow : ''}
          onCancel={() => {
            setTDateValue('')
            setOpened(false)
          }}
          onChange={(event) => {
            handleDateSelect(event.valueText)
            setOpened(false)
          }}
          onPageChange={handleMonthChange || null}
          value={
            tdateValue != '' &&
            tdateValue != '__/__/____' &&
            (tdateValue?.replaceAll('/', '')?.replaceAll('_', '')?.length == 2 ||
              tdateValue?.replaceAll('/', '')?.replaceAll('_', '')?.length < 8)
              ? moment()
                  .month(tdateValue?.replaceAll('/', '')?.replaceAll('_', '')?.slice(0, 2) - 1)
                  .startOf('month')
                  .toDate()
              : value
                ? moment(value)
                : ''
          }
          responsive={{
            xsmall: {
              view: {
                calendar: {
                  type: 'week',
                },
                agenda: {
                  type: 'day',
                },
              },
            },
            custom: {
              display: 'center',
            },
          }}
          marked={originalCalendarData?.length > 0 ? originalCalendarData : markedCalendarDays}
          invalid={invalid}
          buttons={[
            {
              text: 'Today',
              cssClass: 'float-start',
              variant: 'outline',
              handler: () => {
                handleDateSelect(moment().format('MM/DD/yyyy'))
                setOpened(false)
              },
            },
            {
              text: 'X',
              cssClass: 'float-end',
              handler: 'cancel',
            },
          ]}
        />
      )}
    </div>
  )
}

export default memo(MobiScrollCalendar)
